import { BoxGeometry, CylinderGeometry, Group, Mesh } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { Rng } from '../../core/rng';
import type { VehicleSpec } from './types';

const UNIT = new BoxGeometry(1, 1, 1);
const BAG_COLORS = ['#b23a48', '#1d4e89', '#e9c46a', '#6a994e', '#7b2cbf', '#d62828'];

function block(color: string, w: number, h: number, d: number, x: number, y: number, z: number): Mesh {
  const mesh = new Mesh(UNIT, flatMaterial(color));
  mesh.scale.set(w, h, d);
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  return mesh;
}

export function wheels(spec: VehicleSpec): Group {
  const group = new Group();
  const { rear, front, radius } = spec.wheels;
  const tyre = new CylinderGeometry(radius, radius, 0.26, 18);
  const rim = new CylinderGeometry(radius * 0.55, radius * 0.55, 0.28, 12);
  const halfTrack = spec.width / 2 - 0.12;
  for (const u of [rear, front]) {
    for (const side of [-1, 1]) {
      const t = new Mesh(tyre, flatMaterial('#151515'));
      const r = new Mesh(rim, flatMaterial(spec.rimColor ?? '#9a9a9a'));
      for (const m of [t, r]) {
        m.rotation.z = Math.PI / 2;
        m.position.set(side * halfTrack, radius, spec.length / 2 - u);
      }
      group.add(t, r);
    }
  }
  return group;
}

export function bumpersAndMirrors(spec: VehicleSpec): Group {
  const group = new Group();
  const w = spec.width + 0.06;
  group.add(block('#1d1d1d', w, 0.22, 0.14, 0, 0.5, -spec.length / 2 - 0.02));
  group.add(block('#1d1d1d', w, 0.22, 0.14, 0, 0.5, spec.length / 2 + 0.02));
  const ws = spec.profile[spec.windscreen + 1];
  if (ws) {
    for (const side of [-1, 1]) {
      group.add(block('#111', 0.1, 0.28, 0.16, side * (spec.width / 2 + 0.12), ws[1] + 0.25, spec.length / 2 - ws[0] + 0.25));
    }
  }
  return group;
}

/** Roof rack with luggage and "Ghana must go" bags. */
export function roofRack(spec: VehicleSpec, seed: number): Group {
  const group = new Group();
  const top = spec.height;
  const rackLength = spec.length * 0.62;
  const zc = 0.35;
  for (const side of [-1, 1]) group.add(block('#2b2b2b', 0.06, 0.06, rackLength, side * (spec.width / 2 - 0.15), top + 0.12, zc));
  for (let i = 0; i < 5; i += 1) group.add(block('#2b2b2b', spec.width - 0.3, 0.05, 0.05, 0, top + 0.1, zc - rackLength / 2 + (i * rackLength) / 4));
  const rng = new Rng(seed);
  const bags = rng.int(2, 5);
  for (let i = 0; i < bags; i += 1) {
    const w = rng.range(0.5, 0.9);
    const h = rng.range(0.3, 0.55);
    const d = rng.range(0.5, 0.9);
    group.add(block(rng.pick(BAG_COLORS), w, h, d, rng.range(-0.4, 0.4), top + 0.14 + h / 2, zc + rng.range(-rackLength / 2.6, rackLength / 2.6)));
  }
  return group;
}
